/* FabriGroup Yard PWA — operatori di piazzale.
   Flusso: l'addetto inserisce il proprio nome → scansiona QR sul parabrezza (o digita
   targa/id) → tocca un'azione (Pulizia / Check-in / Check-out) per far partire o
   chiudere il timer. La leaderboard mostra in real time il punteggio.
   Mobile-first, full-screen, niente admin layout. */
import { useEffect, useRef, useState } from "react";
import { Camera, Award, Sparkles, CheckCircle2, Clock, Trophy, LogOut, Zap, X } from "lucide-react";
import { api } from "../../lib/api";
import { toast, Toaster } from "sonner";

const ACTIONS = [
  { id: "cleaning",  label: "Pulizia",     accent: "from-cyan-500 to-blue-600",      tip: "Lavaggio + sanificazione" },
  { id: "checkin",   label: "Check-In",    accent: "from-emerald-500 to-green-600",  tip: "Consegna al cliente" },
  { id: "checkout",  label: "Check-Out",   accent: "from-fuchsia-500 to-purple-600", tip: "Rientro veicolo" },
];

const LS_OP = "yard_operator_v1";

export default function YardOperator() {
  const [op, setOp] = useState(() => { try { return JSON.parse(localStorage.getItem(LS_OP) || "null"); } catch { return null; } });
  const [name, setName] = useState("");
  const [carInput, setCarInput] = useState("");
  const [activeJobs, setActiveJobs] = useState({}); // { [carId+kind]: {start, kind, plate} }
  const [leaderboard, setLeaderboard] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  const scannerRef = useRef(null);
  const scannerHostId = "yard-qr-region";

  // tick every second to refresh durations
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const loadLeaderboard = async () => {
    try {
      const r = await api.get("/v3/yard/leaderboard?days=30");
      setLeaderboard(r.data?.leaderboard || []);
    } catch { /* no-op */ }
  };

  useEffect(() => { if (op) loadLeaderboard(); }, [op]);

  const startSession = () => {
    const trimmed = name.trim();
    if (trimmed.length < 2) { toast.error("Inserisci il tuo nome"); return; }
    const o = { id: `op-${trimmed.toLowerCase().replace(/\s+/g, "-")}`, name: trimmed, since: Date.now() };
    localStorage.setItem(LS_OP, JSON.stringify(o));
    setOp(o);
  };

  const logout = () => {
    if (Object.keys(activeJobs).length > 0) {
      if (!window.confirm("Hai timer attivi. Esci comunque?")) return;
    }
    localStorage.removeItem(LS_OP);
    setOp(null);
    setActiveJobs({});
  };

  const stopScanner = async () => {
    try {
      if (scannerRef.current) {
        await scannerRef.current.stop().catch(() => {});
        await scannerRef.current.clear().catch(() => {});
        scannerRef.current = null;
      }
    } catch { /* no-op */ }
    setScanning(false);
  };

  const startScanner = async () => {
    setScanning(true);
    try {
      // Lazy import to keep main bundle small
      const { Html5Qrcode } = await import("html5-qrcode");
      // Wait one frame so the host element exists in DOM
      await new Promise((r) => setTimeout(r, 50));
      const inst = new Html5Qrcode(scannerHostId);
      scannerRef.current = inst;
      await inst.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        (decoded) => {
          setCarInput(decoded);
          stopScanner();
          toast.success(`QR letto: ${decoded.substring(0, 16)}…`);
        },
        () => {} // ignore per-frame errors
      );
    } catch (e) {
      toast.error("Camera non disponibile. Inserisci la targa manualmente.");
      setScanning(false);
    }
  };

  const sendEvent = async (kind, phase) => {
    const carId = carInput.trim();
    if (!carId) { toast.error("Scansiona QR o digita la targa/ID"); return; }
    setBusy(true);
    try {
      const event = `${kind}_${phase}`;
      const r = await api.post("/v3/yard/event", {
        operator_id: op.id,
        operator_name: op.name,
        car_id: carId,
        event,
      });
      const key = `${carId}::${kind}`;
      if (phase === "start") {
        setActiveJobs((prev) => ({ ...prev, [key]: { kind, carId, start: Date.now() } }));
        toast.success(`Timer ${kind} avviato`);
      } else {
        const dur = r.data?.duration_min;
        setActiveJobs((prev) => { const n = { ...prev }; delete n[key]; return n; });
        toast.success(dur != null ? `${kind} chiuso in ${dur.toFixed(2)} min` : `${kind} chiuso`);
        setCarInput("");
        loadLeaderboard();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio evento");
    } finally { setBusy(false); }
  };

  // ---- LOGIN SCREEN ----
  if (!op) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-fuchsia-950/40 flex items-center justify-center p-4">
        <Toaster position="top-center" theme="dark" />
        <div className="w-full max-w-sm bg-slate-900/80 backdrop-blur border border-slate-800 rounded-2xl p-6 shadow-2xl" data-testid="yard-login-card">
          <div className="text-center mb-5">
            <Zap className="h-12 w-12 text-fuchsia-400 mx-auto mb-2" />
            <h1 className="text-2xl font-bold text-white">Yard Operator</h1>
            <p className="text-slate-400 text-sm mt-1">Piazzale FabriGroup · scan & race</p>
          </div>
          <label className="block text-sm text-slate-300 mb-1.5">Il tuo nome</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && startSession()}
            placeholder="es. Mario Rossi"
            autoFocus
            className="w-full bg-slate-950 border border-slate-700 rounded-lg p-3 text-slate-200"
            data-testid="yard-name-input"
          />
          <button
            onClick={startSession}
            className="mt-4 w-full px-4 py-3 rounded-lg bg-gradient-to-r from-fuchsia-600 to-purple-600 text-white font-semibold flex items-center justify-center gap-2"
            data-testid="yard-start-btn"
          >
            <Sparkles className="h-4 w-4" /> Inizia turno
          </button>
          <p className="text-xs text-slate-500 mt-4 text-center">Salviamo solo nome e ID locale del dispositivo. Nessun account richiesto.</p>
        </div>
      </div>
    );
  }

  // ---- MAIN OPERATOR SCREEN ----
  const myRank = leaderboard.findIndex((x) => x.operator_id === op.id);
  const me = myRank >= 0 ? leaderboard[myRank] : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-fuchsia-950/40 text-slate-200">
      <Toaster position="top-center" theme="dark" />

      {/* TOP BAR */}
      <div className="sticky top-0 z-10 bg-slate-950/90 backdrop-blur border-b border-slate-800">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-full bg-gradient-to-br from-fuchsia-500 to-purple-600 flex items-center justify-center text-white font-bold">
              {op.name.charAt(0).toUpperCase()}
            </div>
            <div className="leading-tight">
              <div className="text-sm font-semibold text-white" data-testid="yard-op-name">{op.name}</div>
              <div className="text-xs text-slate-400">
                {me ? <>Score <b className="text-amber-300">{me.gamification_score}</b> · {me.tasks} task · pos {myRank + 1}</> : "Nessun task ancora"}
              </div>
            </div>
          </div>
          <button onClick={logout} className="text-slate-400 hover:text-white p-2" aria-label="Esci" data-testid="yard-logout-btn">
            <LogOut className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 pt-4 pb-20 space-y-5">
        {/* SCANNER / INPUT */}
        <div className="bg-slate-900/70 border border-slate-800 rounded-2xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-white">Veicolo selezionato</span>
            {!scanning ? (
              <button
                onClick={startScanner}
                className="px-3 py-1.5 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-xs flex items-center gap-1.5"
                data-testid="yard-scan-btn"
              >
                <Camera className="h-3.5 w-3.5" /> Scansiona QR
              </button>
            ) : (
              <button onClick={stopScanner} className="px-3 py-1.5 rounded-lg bg-rose-600 text-white text-xs flex items-center gap-1.5">
                <X className="h-3.5 w-3.5" /> Stop
              </button>
            )}
          </div>

          {scanning && (
            <div id={scannerHostId} className="w-full aspect-square bg-black rounded-lg overflow-hidden mb-2" />
          )}

          <input
            value={carInput}
            onChange={(e) => setCarInput(e.target.value)}
            placeholder="Targa o ID veicolo (es. AB123CD)"
            className="w-full bg-slate-950 border border-slate-700 rounded-lg p-3 text-slate-200 text-base font-mono tracking-wide uppercase"
            data-testid="yard-car-input"
          />
        </div>

        {/* ACTION BUTTONS */}
        <div className="space-y-3">
          {ACTIONS.map((a) => {
            const key = `${carInput.trim()}::${a.id}`;
            const job = activeJobs[key];
            const elapsed = job ? Math.floor((Date.now() - job.start) / 1000) : 0;
            const min = Math.floor(elapsed / 60);
            const sec = elapsed % 60;
            return (
              <div key={a.id} className={`rounded-2xl p-4 bg-gradient-to-br ${a.accent} shadow-lg`} data-testid={`yard-action-${a.id}`}>
                <div className="flex items-center justify-between text-white">
                  <div>
                    <div className="text-xl font-bold">{a.label}</div>
                    <div className="text-xs opacity-80">{a.tip}</div>
                  </div>
                  {job && (
                    <div className="flex items-center gap-1.5 bg-black/30 px-3 py-1.5 rounded-full text-sm font-mono" data-testid={`yard-timer-${a.id}`}>
                      <Clock className="h-4 w-4" />
                      {String(min).padStart(2, "0")}:{String(sec).padStart(2, "0")}
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <button
                    disabled={busy || !!job || !carInput.trim()}
                    onClick={() => sendEvent(a.id, "start")}
                    className="px-3 py-2.5 rounded-lg bg-black/30 hover:bg-black/40 text-white text-sm font-semibold disabled:opacity-40"
                    data-testid={`yard-start-${a.id}`}
                  >
                    ▶ Avvia
                  </button>
                  <button
                    disabled={busy || !job}
                    onClick={() => sendEvent(a.id, "end")}
                    className="px-3 py-2.5 rounded-lg bg-white/95 hover:bg-white text-slate-900 text-sm font-semibold disabled:opacity-40 flex items-center justify-center gap-1.5"
                    data-testid={`yard-end-${a.id}`}
                  >
                    <CheckCircle2 className="h-4 w-4" /> Chiudi
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* LEADERBOARD */}
        <div className="bg-slate-900/70 border border-slate-800 rounded-2xl p-4" data-testid="yard-leaderboard">
          <div className="flex items-center gap-2 mb-3">
            <Trophy className="h-5 w-5 text-amber-400" />
            <span className="text-sm font-semibold text-white">Classifica 30 giorni</span>
          </div>
          {leaderboard.length === 0 ? (
            <div className="text-xs text-slate-500 text-center py-6">Ancora nessun task. Sii il primo!</div>
          ) : (
            <div className="space-y-1.5">
              {leaderboard.slice(0, 8).map((o, i) => {
                const isMe = o.operator_id === op.id;
                return (
                  <div key={o.operator_id} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${isMe ? "bg-fuchsia-500/15 border border-fuchsia-500/40" : "bg-slate-800/40"}`}>
                    <span className="w-6 text-center text-slate-400">
                      {i === 0 ? "🏆" : i === 1 ? "🥈" : i === 2 ? "🥉" : i + 1}
                    </span>
                    <span className={`flex-1 truncate ${isMe ? "text-fuchsia-200 font-semibold" : "text-slate-200"}`}>
                      {o.operator_name}{isMe && " (tu)"}
                    </span>
                    <span className="text-xs text-slate-400">{o.tasks} task</span>
                    <span className="px-2 py-0.5 rounded bg-amber-500/15 border border-amber-500/40 text-amber-300 text-xs font-semibold flex items-center gap-1">
                      <Award className="h-3 w-3" /> {o.gamification_score}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
